import React, { ReactNode, SyntheticEvent, useCallback, useMemo, useState } from 'react';

import { Breadcrumbs, Card, CardContent, Divider, Grid, Paper, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import { StyledLoadingButton } from '../../../../../components/forms/LoadingButton';
import { RosterProvider } from '../../../../../context/RosterContext';
import { useQuery } from '../../../../../hooks/useQuery';
import ClassContainer from '../../../../ClassModal/ClassContainer';
import ErrorForm from '../../../../ClassModal/ErrorForm';
import StyledFormTitle from '../../../styles/form-title';
import CopyUrlButton from '../CopyUrlButton';
import { ClassDetailEditForm, RequiredIcon } from './ClassDetailEditForm';
import { ClassDetailLoadingForm } from './ClassDetailLoadingForm';
import { ClassDetailRoster } from './ClassDetailRoster';
import { StyledActionButton } from '../../../../../components/templates/Tables/index.styles';
import DownloadChatModal from '../DownloadChat/DownloadChatModal';

interface TabPanelProps {
    children?: ReactNode;
    dir?: string;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`class-detail-tabpanel-${index}`}
            aria-labelledby={`class-detail-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ mt: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `class-detail-tab-${index}`,
        'aria-controls': `class-detail-tabpanel-${index}`
    };
}

const Title = styled(StyledFormTitle)`
    margin-bottom: 1rem;
`;

export const ClassDetail = () => {
    const { t } = useTranslation(['admin_settings', 'create_edit_class']);
    const { classData, handleSubmit, updateClass, getClassToEditStatus, errorMessage, updateClassStatus, userUuid } = useContainer(ClassContainer);
    const query = useQuery();
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();
    const [tabValue, setTabValue] = useState(0);
    const [downloadChatModalIsOpen, setDownloadChatModalIsOpen] = useState(false);

    const onSubmit = useCallback(async () => {
        await updateClass({
            onSuccess: () => enqueueSnackbar(t('admin_settings:classes.update-success'), { variant: 'success' }),
            onError: () => enqueueSnackbar(t('admin_settings:classes.update-error'), { variant: 'error' })
        });
    }, [enqueueSnackbar, t, updateClass]);

    const renderedForm = useMemo(() => {
        if (getClassToEditStatus === 'SUCCESS') {
            return <ClassDetailEditForm onSubmit={handleSubmit(onSubmit)}/>;
        }
        if (getClassToEditStatus === 'ERROR') {
            return <ErrorForm errorMessage={errorMessage}/>;
        }

        return <ClassDetailLoadingForm/>;
    }, [getClassToEditStatus, handleSubmit, onSubmit, errorMessage]);

    const handleTabChange = (event: SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    return (
        <>
            <Grid container justifyContent="space-between">
                <Grid item>
                    <Title>{t('admin_settings:classes.title')}</Title>
                </Grid>
                <Grid>
                    <StyledActionButton onClick={() => setDownloadChatModalIsOpen(true)} variant="outlined">{t('admin_settings:classes.download-chat')}</StyledActionButton>
                    <CopyUrlButton classUuid={classData?.class_uuid || ''}/>
                    <StyledLoadingButton loading={updateClassStatus === 'LOADING'} variant="contained" onClick={handleSubmit(onSubmit)}>{t('create_edit_class:save')}</StyledLoadingButton>
                </Grid>
            </Grid>

            <Breadcrumbs aria-label="breadcrumb">
                <Link to="/adminSettings/classes">{t('admin_settings:classes.all-classes')}</Link>
                <Typography color="text.primary">{classData?.class_name || query.get('className')}</Typography>
            </Breadcrumbs>

            <Paper elevation={0} sx={{ mt: 2 }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="fullWidth"
                    aria-label={t('admin_settings:classes.class-detail-tabs')}
                >
                    <Tab label={t('admin_settings:classes.details')} {...a11yProps(0)} />
                    <Tab label={t('admin_settings:classes.roster')} {...a11yProps(1)} />
                </Tabs>
            </Paper>

            <TabPanel value={tabValue} index={0} dir={theme.direction}>
                <Card elevation={0} sx={{ overflow: 'visible', padding: 0 }}>
                    <CardContent>
                        <Typography><RequiredIcon/> {t('admin_settings:classes.required-fields')}</Typography>
                        <Divider sx={{ my: 2 }}/>
                        {renderedForm}
                    </CardContent>
                </Card>
            </TabPanel>
            <TabPanel value={tabValue} index={1} dir={theme.direction}>
                <Card elevation={0} sx={{ overflow: 'visible', padding: 0 }}>
                    <CardContent>
                        {classData &&
                            <RosterProvider
                                classId={classData.sid as unknown as string}
                                classUuid={classData.class_uuid}
                                userUuid={window.localStorage?.user_uuid as string}
                                isDemo={'false'}
                            >
                                <ClassDetailRoster/>
                            </RosterProvider>
                        }
                    </CardContent>
                </Card>
            </TabPanel>
            <DownloadChatModal
                isOpen={downloadChatModalIsOpen}
                onClose={() => setDownloadChatModalIsOpen(false)}
                classId={classData?.sid}
                className={classData?.class_name}
                userUuid={userUuid}
            />
        </>
    );
};
